import { makeObservable, observable, computed, action } from 'mobx';
import { BaseService, EventSubscription } from './base/BaseService';
import { AudioErrorCode, AudioEvent, AudioServiceError, AudioServiceObserver, AudioState, IAudioService, PlaybackRate } from './interfaces/IAudioService';

/**
 * Service for managing audio playback for the transcript editor
 */
export class AudioService extends BaseService<AudioEvent> implements IAudioService {
  // Core audio state
  private _audioElement: HTMLAudioElement | null = null;
  private _isLoaded: boolean = false;
  private _state: AudioState = AudioState.STOPPED;
  private _isLoading: boolean = false;
  private _currentTime: number = 0;
  private _duration: number = 0;
  private _playbackRate: PlaybackRate = PlaybackRate.NORMAL;
  private _volume: number = 1.0;
  private _muted: boolean = false;
  private _audioUrl: string | null = null;
  
  // Waveform data
  private _waveformData: number[] | null = null;
  
  // For backward compatibility with tests
  private _observers: Set<AudioServiceObserver> = new Set();
  
  // Update interval for time tracking
  private _updateInterval: number | null = null;
  
  /**
   * Creates a new AudioService instance
   */
  constructor() {
    super(false); // Do not use makeAutoObservable in BaseService
    
    // Use makeObservable with no configuration for now
    makeObservable(this);
    
    this._createAudioElement();
  }
  
  /**
   * Creates the audio element
   */
  private _createAudioElement(): void {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    
    try {
      this._audioElement = document.createElement('audio');
      this._audioElement.preload = 'auto';
      
      // Set up event listeners
      this._setupEventListeners();
    } catch (error) {
      console.error('Failed to create audio element:', error);
      this.notify(AudioEvent.ERROR, {
        error: new AudioServiceError(
          'Failed to create audio element', 
          AudioErrorCode.BROWSER_UNSUPPORTED
        )
      });
    }
  }
  
  /**
   * Sets up event listeners for the audio element
   */
  private _setupEventListeners(): void {
    if (!this._audioElement) return;
    
    this._audioElement.addEventListener('play', () => {
      this._state = AudioState.PLAYING;
      this._startTimeUpdates();
      this.notify(AudioEvent.STATE_CHANGE, this._state);
    });
    
    this._audioElement.addEventListener('pause', () => {
      this._state = AudioState.PAUSED;
      this._stopTimeUpdates();
      this.notify(AudioEvent.STATE_CHANGE, this._state);
    });
    
    this._audioElement.addEventListener('ended', () => {
      this._state = AudioState.STOPPED;
      this._stopTimeUpdates();
      this.notify(AudioEvent.STATE_CHANGE, this._state);
    });
    
    this._audioElement.addEventListener('timeupdate', () => {
      if (this._audioElement) {
        this._currentTime = this._audioElement.currentTime;
        this.notify(AudioEvent.TIME_UPDATE, this._currentTime);
      }
    });
    
    this._audioElement.addEventListener('durationchange', () => {
      if (this._audioElement) {
        this._duration = this._audioElement.duration;
        this.notify(AudioEvent.DURATION_CHANGE, this._duration);
      }
    });
    
    this._audioElement.addEventListener('volumechange', () => {
      if (this._audioElement) {
        this._volume = this._audioElement.volume;
        this._muted = this._audioElement.muted;
        this.notify(AudioEvent.VOLUME_CHANGE, this._volume);
      }
    });
    
    this._audioElement.addEventListener('error', (e) => {
      this._state = AudioState.ERROR;
      this._stopTimeUpdates();
      
      const errorDetails = this._audioElement?.error ? 
        `Code: ${this._audioElement.error.code}, Message: ${this._audioElement.error.message}` : 
        'Unknown error';
        
      const error = new AudioServiceError(
        `Audio playback error: ${errorDetails}`,
        AudioErrorCode.PLAYBACK_FAILED
      );
      
      console.error('AudioService error:', error);
      this.notify(AudioEvent.ERROR, { error });
    });
  }
  
  /**
   * Starts periodic time updates
   */
  private _startTimeUpdates(): void {
    this._stopTimeUpdates();
    
    this._updateInterval = window.setInterval(() => {
      if (this._audioElement) {
        this._currentTime = this._audioElement.currentTime;
        this.notify(AudioEvent.TIME_UPDATE, this._currentTime);
      }
    }, 30); // Update 30 times per second for smooth UI
  }
  
  /**
   * Stops periodic time updates
   */
  private _stopTimeUpdates(): void {
    if (this._updateInterval) {
      window.clearInterval(this._updateInterval);
      this._updateInterval = null;
    }
  }
  
  /**
   * Override the notify method to also notify legacy observers
   * 
   * @param event Event that occurred
   * @param data Data associated with the event
   */
  protected override notify(event: AudioEvent, data: any): void {
    // Call the base class notify method
    super.notify(event, data);
    
    // Notify legacy observers
    for (const observer of this._observers) {
      try {
        observer.onAudioEvent(event, data);
      } catch (error) {
        console.error(`Error in AudioService observer for ${event}:`, error);
      }
    }
  }
  
  /**
   * Register an event listener
   * 
   * @param event Event to listen for
   * @param callback Callback to invoke when event occurs
   * @returns Subscription object for easy unsubscribing
   */
  override on(event: AudioEvent, callback: (data: any) => void): EventSubscription {
    return super.on(event, callback);
  }
  
  /**
   * Register an observer - for backward compatibility with tests
   * 
   * @param observer Observer to register
   */
  registerObserver(observer: AudioServiceObserver): void {
    this._observers.add(observer);
  }
  
  /**
   * Unregister an observer - for backward compatibility with tests
   * 
   * @param observer Observer to unregister
   */
  unregisterObserver(observer: AudioServiceObserver): void {
    this._observers.delete(observer);
  }
  
  /**
   * Loads an audio file
   * 
   * @param url URL of the audio file
   * @returns Promise resolving to true if audio was loaded successfully
   * @throws AudioServiceError if loading fails
   */
  async loadAudio(url: string): Promise<boolean> {
    if (!url) {
      const error = new AudioServiceError(
        'Invalid or empty audio URL',
        AudioErrorCode.INVALID_URL
      );
      this.notify(AudioEvent.ERROR, { error });
      throw error;
    }
    
    if (!this._audioElement) {
      this._createAudioElement();
      
      if (!this._audioElement) {
        const error = new AudioServiceError(
          'Audio element not available', 
          AudioErrorCode.BROWSER_UNSUPPORTED
        );
        this.notify(AudioEvent.ERROR, { error });
        throw error;
      }
    }
    
    this._audioUrl = url;
    this._isLoading = true;
    this.notify(AudioEvent.LOADING, true);
    
    return new Promise((resolve, reject) => {
      if (!this._audioElement) {
        const error = new AudioServiceError(
          'Audio element not available', 
          AudioErrorCode.BROWSER_UNSUPPORTED
        );
        this._isLoading = false;
        this.notify(AudioEvent.LOADING, false);
        this.notify(AudioEvent.ERROR, { error });
        reject(error);
        return;
      }
      
      // Set up event listeners for loading
      const onCanPlay = () => {
        this._isLoaded = true;
        this._isLoading = false;
        this._duration = this._audioElement?.duration || 0;
        this._state = AudioState.PAUSED;
        
        this._audioElement?.removeEventListener('canplay', onCanPlay);
        this._audioElement?.removeEventListener('error', onError);
        
        this.notify(AudioEvent.LOADING, false);
        this.notify(AudioEvent.DURATION_CHANGE, this._duration);
        this.notify(AudioEvent.STATE_CHANGE, this._state);
        // Add AUDIO_LOADED event for test compatibility
        this.notify(AudioEvent.AUDIO_LOADED, { url });
        
        resolve(true);
      };
      
      const onError = (e: Event) => {
        const errorDetails = this._audioElement?.error ? 
          `Code: ${this._audioElement.error.code}, Message: ${this._audioElement.error.message}` : 
          'Unknown error';
        
        const error = new AudioServiceError(
          `Failed to load audio: ${errorDetails}`,
          AudioErrorCode.LOAD_FAILED
        );
        
        this._audioElement?.removeEventListener('canplay', onCanPlay);
        this._audioElement?.removeEventListener('error', onError);
        
        this._isLoading = false;
        this._state = AudioState.ERROR;
        
        this.notify(AudioEvent.LOADING, false);
        this.notify(AudioEvent.STATE_CHANGE, this._state);
        this.notify(AudioEvent.ERROR, { error });
        
        reject(error);
      };
      
      this._audioElement.addEventListener('canplay', onCanPlay);
      this._audioElement.addEventListener('error', onError);
      
      try {
        // Set CORS attribute to handle cross-origin requests
        this._audioElement.crossOrigin = 'anonymous';
        
        // Reset current time and playback state
        this._currentTime = 0;
        this._state = AudioState.LOADING;
        
        // Load the audio
        this._audioElement.src = url;
        this._audioElement.load();
        
        // Add a timeout in case the audio never loads
        setTimeout(() => {
          if (this._audioElement && this._audioElement.readyState === 0) {
            const error = new AudioServiceError(
              'Audio loading timed out after 10 seconds',
              AudioErrorCode.LOAD_FAILED
            );
            
            this._audioElement.removeEventListener('canplay', onCanPlay);
            this._audioElement.removeEventListener('error', onError);
            
            this._isLoading = false;
            this._state = AudioState.ERROR;
            
            this.notify(AudioEvent.LOADING, false);
            this.notify(AudioEvent.STATE_CHANGE, this._state);
            this.notify(AudioEvent.ERROR, { error });
            
            reject(error);
          }
        }, 10000);
      } catch (err) {
        const error = new AudioServiceError(
          `Exception during audio loading: ${err}`,
          AudioErrorCode.LOAD_FAILED
        );
        
        this._audioElement.removeEventListener('canplay', onCanPlay);
        this._audioElement.removeEventListener('error', onError);
        
        this._isLoading = false;
        this._state = AudioState.ERROR;
        
        this.notify(AudioEvent.LOADING, false);
        this.notify(AudioEvent.STATE_CHANGE, this._state);
        this.notify(AudioEvent.ERROR, { error });
        
        reject(error);
      }
    });
  }
  
  /**
   * Loads waveform data for visualization
   * 
   * @param url URL of the waveform data or an array of waveform data
   * @returns Promise resolving to true if waveform data was loaded successfully
   */
  async loadWaveformData(url: string | number[]): Promise<boolean> {
    // If input is an array, use it directly
    if (Array.isArray(url)) {
      this._waveformData = [...url];
      this.notify(AudioEvent.WAVEFORM_DATA_LOADED, this._waveformData);
      return true;
    }
    
    // Otherwise, treat as URL
    if (!url) {
      return false;
    }
    
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        // Check different data formats - some APIs wrap in data property, some don't
        this._waveformData = data.data || data || [];
        this.notify(AudioEvent.WAVEFORM_DATA_LOADED, this._waveformData);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to load waveform data:', error);
      return false;
    }
  }
  
  /**
   * Starts or resumes audio playback
   * 
   * @returns True if playback started successfully, false otherwise
   */
  play(): boolean {
    if (!this._audioElement || !this._isLoaded) {
      return false;
    }
    
    try {
      // Add PLAYBACK_STARTED event for test compatibility
      this.notify(AudioEvent.PLAYBACK_STARTED, {});
      this._audioElement.play();
      return true;
    } catch (error) {
      console.error('Error playing audio:', error);
      
      const serviceError = new AudioServiceError(
        'Failed to start playback',
        AudioErrorCode.PLAYBACK_FAILED
      );
      
      this.notify(AudioEvent.ERROR, { error: serviceError });
      return false;
    }
  }
  
  /**
   * Pauses audio playback
   * 
   * @returns True if pause was successful, false otherwise
   */
  pause(): boolean {
    if (!this._audioElement || !this._isLoaded) {
      return false;
    }
    
    try {
      // Add PLAYBACK_PAUSED event for test compatibility
      this.notify(AudioEvent.PLAYBACK_PAUSED, {});
      this._audioElement.pause();
      return true;
    } catch (error) {
      console.error('Error pausing audio:', error);
      return false;
    }
  }
  
  /**
   * Toggles play/pause state
   * 
   * @returns New play state (true if playing, false if paused)
   */
  togglePlayPause(): boolean {
    if (this._state === AudioState.PLAYING) {
      this.pause();
      return false;
    } else {
      this.play();
      return true;
    }
  }
  
  /**
   * Seeks to a specific time in the audio
   * 
   * @param time Time in seconds to seek to
   * @returns True if seek was successful, false otherwise
   */
  seekTo(time: number): boolean {
    if (!this._audioElement || !this._isLoaded) {
      return false;
    }
    
    try {
      // Ensure time is within valid range
      const validTime = Math.max(0, Math.min(time, this._duration));
      this._audioElement.currentTime = validTime;
      this._currentTime = validTime;
      this.notify(AudioEvent.TIME_UPDATE, this._currentTime);
      
      return true;
    } catch (error) {
      console.error('Error seeking audio:', error);
      
      const serviceError = new AudioServiceError(
        'Failed to seek to specified time',
        AudioErrorCode.INVALID_TIME
      );
      
      this.notify(AudioEvent.ERROR, { error: serviceError });
      return false;
    }
  }
  
  /**
   * Sets the playback rate
   * 
   * @param rate Playback rate to set
   * @returns True if rate was set successfully, false otherwise
   */
  setPlaybackRate(rate: PlaybackRate): boolean {
    if (!this._audioElement) {
      return false;
    }
    
    try {
      this._audioElement.playbackRate = rate;
      this._playbackRate = rate;
      return true;
    } catch (error) {
      console.error('Error setting playback rate:', error);
      return false;
    }
  }
  
  /**
   * Sets the audio volume
   * 
   * @param volume Volume between 0.0 and 1.0
   * @returns True if volume was set successfully, false otherwise
   */
  setVolume(volume: number): boolean {
    if (!this._audioElement) {
      return false;
    }
    
    try {
      // Ensure volume is within valid range
      const validVolume = Math.max(0, Math.min(volume, 1));
      this._audioElement.volume = validVolume;
      this._volume = validVolume;
      this.notify(AudioEvent.VOLUME_CHANGE, this._volume);
      return true;
    } catch (error) {
      console.error('Error setting volume:', error);
      return false;
    }
  }
  
  /**
   * Mutes or unmutes the audio
   * 
   * @param muted Whether to mute the audio
   * @returns True if mute state was set successfully, false otherwise
   */
  setMuted(muted: boolean): boolean {
    if (!this._audioElement) {
      return false;
    }
    
    try {
      this._audioElement.muted = muted;
      this._muted = muted;
      return true;
    } catch (error) {
      console.error('Error setting muted state:', error);
      return false;
    }
  }
  
  /**
   * Cleans up the audio service resources
   */
  override cleanup(): void {
    this._stopTimeUpdates();
    
    if (this._audioElement) {
      this._audioElement.pause();
      this._audioElement.src = '';
      this._audioElement.remove();
      this._audioElement = null;
    }
    
    this._isLoaded = false;
    this._state = AudioState.STOPPED;
    this._isLoading = false;
    this._currentTime = 0;
    this._duration = 0;
    this._audioUrl = null;
    this._observers.clear();
    
    // Call base class cleanup to clear listeners
    super.cleanup();
  }
  
  // State query methods
  getState(): AudioState { return this._state; }
  getCurrentTime(): number { return this._currentTime; }
  getDuration(): number { return this._duration; }
  getVolume(): number { return this._volume; }
  getWaveformData(): number[] | null { return this._waveformData ? [...this._waveformData] : null; }
  isLoading(): boolean { return this._isLoading; }
  
  // Public getters for MobX
  get isLoaded(): boolean { return this._isLoaded; }
  get state(): AudioState { return this._state; }
  get isPlaying(): boolean { return this._state === AudioState.PLAYING; }
  get currentTime(): number { return this._currentTime; }
  get duration(): number { return this._duration; }
  get playbackRate(): PlaybackRate { return this._playbackRate; }
  get volume(): number { return this._volume; }
  get muted(): boolean { return this._muted; }
  get audioUrl(): string | null { return this._audioUrl; }
  get waveformData(): number[] | null { return this._waveformData ? [...this._waveformData] : null; }
}